// ═══════════════════════════════════════════════════════════════════════════
//  THE CAST SCREEN — a member of Synod votes. `#ballot/<ballotId>`.
//
//  This is the second surface in CCA-decides that works on real data and the
//  second that writes. It owns the whole flow for one ballot: issue the paper,
//  hold the mark locally, submit it once, and then say so without saying what it
//  was.
//
//  ── EXACTLY TWO REQUESTS, AND THE FIRST IS NOT UNDER TIME PRESSURE ─────────
//  `POST …/paper` when the screen opens, `POST …/marks` when the member submits.
//  Nothing between them touches the network: marking is free, silent and local
//  (CAST-PATH.md:211), so a hall full of phones on poor wifi makes one request
//  each at the start and one each at the end, and a member deciding slowly is
//  not a member holding a connection open.
//
//  ── A BLANK PAPER IS OFFERED, WITH A WARNING IN FRONT OF IT ────────────────
//  Registrar ruling 2026-08-27. It is a SEPARATE act rather than a fourth row,
//  and the whole argument — why that is the No Confidence rule applied and not
//  broken, and why the warning cannot leak a choice — is at the head of
//  ./BlankConfirm.tsx. Two things about it belong here, because they are this
//  file's: the warning is its own arm of `Screen`, so the rows are UNMOUNTED
//  while it is up; and it still costs exactly one request, so the "two requests"
//  rule above is unchanged.
//
//  ── THE CONFIRMATION REPLACES THE PAPER, IT DOES NOT ANNOTATE IT ───────────
//  A recorded vote unmounts the form. Leaving the marked row on screen beneath a
//  line saying "recorded" would be showing the member their vote after submit —
//  the same disclosure as printing it back, by a slower route, and in the exact
//  posture CAST-PATH.md:213 describes: the phone held loosely while the person
//  looks up. So the marks leave the DOM at the moment they are recorded.
//
//  ── IT PUTS TWO KINDS OF PAPER, AND REFUSES THE SHAPES IT CANNOT ───────────
//  An ELECTION (./PositionPaper) and a MOTION (./MotionPaper) — two papers, one
//  flow, and `inspect` below is the whole of the difference: it narrows the one
//  markable item to one kind or refuses it. A MOTION IS NO LONGER A REFUSAL, and
//  the entry that made it one is gone rather than left unreachable.
//
//  What is still refused, each because it is a shape this screen cannot put to a
//  member CORRECTLY: more than one item this House may decide; an election
//  filling more than one place, or counted by any method but first-past-the-post;
//  a motion whose majority base, instrument or columns this build cannot read;
//  a motion whose majority is measured in each House on a ballot that counts in
//  one tally, which is a ballot nothing could count afterwards; and any other
//  word this build cannot name. Each fails LOUD and closed — a radio group on a
//  three-seat election would silently under-vote every paper on it, and a member
//  would never know; a defaulted majority base would print "a simple majority of
//  those who vote" on a two-thirds amendment, which is the worst wrong sentence
//  this product can print. The refusals are what keep the gap honest rather than
//  hidden.
//
//  ── AN ITEM THIS HOUSE MAY NOT DECIDE IS SHOWN, UNMARKABLE, NEVER OMITTED ──
//  Registrar ruling 2026-08-20, and it is the route's shape as well as this
//  screen's: every item on the ballot is served to every elector carrying
//  `entitled`, because a paper that was simply SHORT of an item would itself be
//  a discriminator. So the gates below run over the ENTITLED items, the rest are
//  listed with the reason they are not the reader's, and a paper on which
//  nothing at all is markable carries no submit control — an empty submission is
//  a lawful request that would write a `participation` row, permanently, for a
//  member who had nothing to decide.
//
//  ── NO FIGURE, NO CLOCK, NO TALLY ──────────────────────────────────────────
//  Nothing on this screen is a number. Not a countdown (a ticking clock in a
//  contested election is a pressure device), not a turnout, not a tally, not a
//  count of how many have voted. The types it reads carry none, so there is none
//  here to leak.
// ═══════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { PortalHeader } from '@cca/ui';
import type { BallotMode } from '@cca/decides-count';
import { useFocusOnMount } from '../a11y/useFocusOnMount';
import type { CastAct } from '../CastStatus';
import { ModeBand } from '../results/ModeBand';
import {
  asBallotMode,
  asSecrecy,
  instrumentParagraphs,
  isElectorate,
  MOTION_ROWS,
  positionRows,
  type MarkableItem,
  type MarkableMotion,
  type PaperSecrecy,
} from '../paper';
import { isMajorityBase } from '../thresholds';
import { positionNotVotableInApp } from '../cast-votability';
import { portalNameFor } from '../portal-name';
import {
  api,
  DecidesApiError,
  type CastItem,
  type CastMark,
  type CastPaperView,
  type Identity,
} from '../api/client';
import { PositionPaper, UnmarkableItems } from './PositionPaper';
import { MotionPaper } from './MotionPaper';
import { BlankConfirm } from './BlankConfirm';

/**
 * A refusal turned into a sentence a person can act on. The wire codes are
 * machine-facing and deliberately distinct so an operator can tell different
 * problems apart; not one of them is a sentence to show a member.
 *
 * **These sentences are for the WHOLE-SCREEN refusal only** — the states in
 * which no paper is on the screen. Most submit-time refusals are mapped to a
 * {@link CastAct} token instead (see `refusalAct`), because that message goes
 * into a live region, and nothing that came off the wire goes into a live region
 * on this path. The one exception is `no_entitled_items`, which is answered by
 * `POST …/marks` and yet belongs here: it means the paper on screen is stale, so
 * the paper is taken down and this is what stands in its place. See its entry.
 *
 * Every one of them ends by saying that nothing was recorded. A member who has
 * just been refused needs that fact before anything else.
 *
 * ── IT IS THE ISSUE ROUTE'S OWN VOCABULARY, RECONCILED 2026-08-27 ──────────
 * `POST …/paper` refuses with exactly six words — `unauthorized` (401),
 * `not_found` (404), `unknown_identity` / `ambiguous_identity` / `not_on_roll`
 * (403, from `resolveElector`) and `ballot_not_open` (409) — plus `bad_request`,
 * which only the marks route emits and which is kept here because the fallback
 * would say "the ballot refused that" about a malformed request. **A refusal
 * with no copy is a blank screen at the moment of a binding vote**, so the map
 * is checked against the worker rather than grown by guesswork: five entries
 * that named nothing (`no_standing`, `not_an_elector`, `ineligible_house`,
 * `voting_closed`, `roll_not_frozen`) were removed with the three real ones
 * added, and `ineligible_house` was doctrinally wrong as well as absent — an
 * item this House may not decide is served UNMARKABLE and never refuses the
 * paper.
 */
const REFUSAL_COPY: Record<string, string> = {
  unauthorized:
    'Your session has ended. Sign in again — no paper has been recorded, and your ballot is still open to you.',
  not_found: 'There is no ballot with that identifier. Nothing has been recorded.',
  unknown_identity:
    'The address you signed in with is not matched to anybody on the roll for this sitting, so there is no paper for you here. Nothing has been recorded. If that is unexpected, speak to the Synod Admin — this screen cannot seat you.',
  // THE SHARED PARISH MAILBOX, and the one refusal here that is a fault in the
  // RECORDS rather than a fact about the reader: one address on two people. It
  // says so plainly, because a member told only "you cannot vote" has nothing to
  // take to the Admin, and this is fixable at the desk in a minute.
  ambiguous_identity:
    'The address you signed in with belongs to more than one person on this roll, so this platform cannot tell whose paper to issue. Nothing has been recorded. Tell the Synod Admin — this is a record to be corrected, not a decision about you.',
  not_on_roll:
    'You are not on the roll for this sitting, so there is no paper for you here. Nothing has been recorded. If that is unexpected, speak to the Synod Admin — this screen cannot seat you.',
  ballot_not_open:
    'This ballot is not open. Nothing has been recorded, and nothing you do here would be.',
  bad_request: 'That request was not understood. Nothing has been recorded.',
  // A SUBMIT-TIME REFUSAL THAT IS ANSWERED BY UNMOUNTING THE PAPER, which is why
  // it has copy HERE rather than a {@link CastAct} token. `POST …/marks` answers
  // 409 `no_entitled_items` when nothing on the ballot is this elector's to mark,
  // and A2 cannot answer it — a paper is issued to every elector on the roll
  // whatever their House. So the only road to this sentence is a standing that
  // CHANGED between issue and cast, which is a real state and not a hypothetical:
  // `effective_voter` reads `roll_amendment` live, and a member may be
  // substituted mid-sitting. The paper on screen is then describing a roll that
  // has moved, so it is removed rather than annotated, and "Try again" re-issues
  // it — which is what shows the member the correct unmarkable screen.
  no_entitled_items:
    'There is nothing on this ballot for you to mark, so there is no paper of yours to record. Nothing has been recorded. This can happen if your place on the roll changed while the ballot was open — if that is unexpected, speak to the Synod Admin.',
  // NOT a wire code — this one is manufactured by the screen when the request
  // never reached a server at all, and it is here rather than left to the
  // fallback because "refused" would be a false statement about a hall with bad
  // wifi, and the wrong one to make to somebody who has not voted yet.
  unreachable:
    'The ballot could not be reached. Nothing has been recorded — this is a problem with the connection, not with your standing to vote. Try again in a moment.',
};

/** The fallback names the BALLOT rather than the portal: the portal's name
 *  depends on who is reading, and a sentence naming a different thing from the
 *  header above it reads as a fault. Every call that reaches here is a ballot
 *  call. */
const refusalSentence = (code: string): string =>
  REFUSAL_COPY[code] ?? 'The ballot refused that. Nothing has been recorded.';

/**
 * A paper this screen will not put to a member.
 *
 * Each of these is a shape the demo build cannot mark CORRECTLY, and each is
 * refused rather than approximated. The cost of the refusal is that a member
 * sees a wall; the cost of the approximation is a paper recorded differently
 * from the way the member meant it, on a binding synod election, with nothing on
 * the screen to say so.
 */
type UnsupportedReason = 'multi-item' | 'multi-seat' | 'method' | 'uncountable' | 'unreadable';

const UNSUPPORTED_COPY: Record<UnsupportedReason, string> = {
  // ONE MOTION OR ONE POSITION PER BALLOT is the registrar's own configuration,
  // so this refusal is deliberate rather than a limit waiting to be lifted.
  'multi-item':
    'This ballot puts more than one thing to you, and this screen can only put one. Nothing has been shown to you and nothing has been recorded. Tell the Synod Admin.',
  'multi-seat':
    'This election fills more than one place. Marking a single row would under-vote your paper, so this screen refuses to show it rather than record a paper you did not mean. Nothing has been recorded. Tell the Synod Admin.',
  method:
    'This election is counted by a method this screen cannot yet put to you. Nothing has been recorded. Tell the Synod Admin.',
  // A BALLOT THAT COULD NOT BE COUNTED AFTERWARDS, refused BEFORE anybody votes.
  // §15.2/§18.1 make voting by Houses MANDATORY under a `summoned-per-house`
  // majority, and the engine refuses that combination `by-houses-required` —
  // FATALLY, and only at the count. So a motion on that base sitting on a
  // `one-tally` ballot is a poll the whole Synod would complete and no
  // scrutineer could ever count, discovered after the votes were cast and could
  // not be re-taken in the same words. It is a distinct word from `unreadable`
  // because the repair is different and the Admin needs to know which: the
  // motion is fine, the BALLOT's house rule is wrong.
  uncountable:
    "This motion's majority is measured in each House separately, but this ballot is set to count in one tally, so the votes on it could not be counted. Nothing has been recorded, and nothing you do here would be. Tell the Synod Admin.",
  // The one refusal here that is about the DATA rather than the shape. Every
  // word it covers is fenced by a CHECK in the schema, so reaching it means a
  // ballot was configured through some other door — which is precisely when a
  // screen must not improvise.
  unreadable:
    'This ballot did not arrive in a shape this screen can read, so it will not put a paper to you that it cannot describe correctly. Nothing has been recorded. Tell the Synod Admin.',
};

/**
 * WHAT THIS SCREEN IS PUTTING TO THE MEMBER — one item, narrowed to its kind.
 *
 * It is the output of {@link inspect} and the payload of the two marking arms of
 * {@link Screen}, which is deliberate: the value the gates approved and the
 * value the paper is rendered from are the SAME value, so there is no second
 * place where a motion could be handed to the election paper.
 */
type Subject =
  { kind: 'position'; item: MarkableItem } | { kind: 'motion'; motion: MarkableMotion };

/**
 * What is on the screen. `recorded` is reached by three roads — a successful
 * submission, a resumed paper that already carries marks, and a `409
 * already_voted` — and it says the same calm thing on all three, because they
 * are the same fact about this member's vote.
 */
type Screen =
  | { status: 'loading' }
  | { status: 'refused'; code: string }
  | { status: 'unsupported'; reason: UnsupportedReason }
  | { status: 'marking'; paper: CastPaperView; subject: Subject; mode: BallotMode }
  /**
   * THE BLANK-PAPER WARNING, and it is an ARM rather than a flag on `marking`
   * for one structural reason: an arm cannot render the rows. A modal over the
   * paper would leave the marked row and its 3px stripe on screen at the highest
   * shoulder-surf moment in the flow, which is the disclosure CAST-PATH.md:213
   * forbids. It carries the paper and the subject so that cancelling returns to
   * the exact screen it replaced — and `marked` lives outside this union, so
   * backing out costs the member nothing.
   */
  | { status: 'confirm-blank'; paper: CastPaperView; subject: Subject; mode: BallotMode }
  /** A paper on which NOTHING is this House's to mark — every item is
   *  `entitled: false`. A lawful state and not a fault, so it is deliberately
   *  not one of the refusals: the items are listed, unmarkable, and there is no
   *  submit control on the screen at all. */
  | { status: 'unmarkable'; paper: CastPaperView; mode: BallotMode }
  /**
   * `mode` and `secrecy` are null where they are genuinely unknown — a 409
   * answered before any paper was seen. The rehearsal band is not optional
   * decoration and the sentence about publication is not decoration either, so
   * each is drawn when it is known and never guessed when it is not.
   */
  | {
      status: 'recorded';
      mode: BallotMode | null;
      secrecy: PaperSecrecy | null;
      already: boolean;
    };

/**
 * What this screen may do with the items on a paper.
 *
 * **`none-entitled` is not a refusal**, and keeping it out of
 * {@link UnsupportedReason} is the point: a lay member holding a paper whose
 * every position is elected by the Clergy has been served exactly correctly, and
 * telling them the ballot was broken would be false.
 */
type Inspection =
  Subject | { kind: 'none-entitled' } | { kind: 'unsupported'; reason: UnsupportedReason };

/**
 * Whether this paper is one the screen can mark, or which refusal it earns.
 *
 * **THE GATES RUN OVER THE ENTITLED ITEMS ONLY, and that is the reconciliation
 * of 2026-08-27.** The route serves EVERY item on the ballot to every elector,
 * each carrying `entitled` — a paper that was simply short of an item would
 * itself be a discriminator (registrar ruling 2026-08-20) — so a ballot with a
 * clergy seat and a lay seat is two items on everybody's paper. Counting all of
 * them would have refused that ballot to the whole Synod as `multi-item`; and on
 * a paper whose single item was NOT this House's, this screen would have drawn a
 * full set of radio rows for a position the database refuses to record a mark
 * on — which is the thing `mark_subject_is_in_the_item_electorate`'s own note
 * forbids a surface from doing: "a screen that offers a control the database
 * refuses teaches an operator — or a member, mid-vote — to work around it."
 *
 * The unentitled items are not dropped; they are rendered UNMARKABLE by
 * {@link UnmarkableItems}. This function decides only what may be MARKED.
 */
function inspect(paper: CastPaperView): Inspection {
  const entitled = paper.items.filter((item) => item.entitled);
  const [item] = entitled;
  if (item === undefined) return { kind: 'none-entitled' };
  if (entitled.length !== 1) return { kind: 'unsupported', reason: 'multi-item' };
  // THE TWO KINDS SPLIT HERE, AND AN UNKNOWN THIRD WORD IS `unreadable` rather
  // than either. `kind` is a plain `TEXT` column fenced to two words by a CHECK,
  // so a third means the ballot was configured through some other door — and the
  // previous shape of this line (`!== 'position'` → a motion) would now send it
  // to the motion paper, which is precisely the guessing this function exists to
  // prevent.
  if (item.kind === 'position') return inspectPosition(item);
  if (item.kind === 'motion') return inspectMotion(item, paper.ballot);
  return { kind: 'unsupported', reason: 'unreadable' };
}

/** An ELECTION — the gates that were this whole function before the motion
 *  paper existed, unchanged but for the `kind` test that now precedes them. */
function inspectPosition(item: CastItem): Inspection {
  // `seats` and `alternateSeats` are NULL on a MOTION and are declared nullable
  // for that reason alone; a position carrying either as NULL is a ballot
  // configured through some door this screen cannot reason about.
  if (item.seats === null || item.alternateSeats === null) {
    return { kind: 'unsupported', reason: 'unreadable' };
  }
  // Places, then method — the rule the ballot builder's "not yet votable" note
  // reads too, written once in `../cast-votability.ts` so the refusal here and the
  // note there cannot disagree (cca-89su).
  const notVotable = positionNotVotableInApp({
    method: item.method,
    seats: item.seats,
    alternateSeats: item.alternateSeats,
  });
  if (notVotable !== null) return { kind: 'unsupported', reason: notVotable };
  // Last, because it is the one that would otherwise be silent: an electorate
  // this build cannot name has no SO 15.8 sentence to put above the rows, and a
  // position paper that says nothing about who elects it is not a paper.
  if (!isElectorate(item.electorate)) return { kind: 'unsupported', reason: 'unreadable' };
  return {
    kind: 'position',
    item: {
      itemId: item.itemId,
      title: item.title,
      electorate: item.electorate,
      alternateSeats: item.alternateSeats,
      candidates: item.candidates,
    },
  };
}

/**
 * A MOTION — and every arm of it REFUSES rather than approximates.
 *
 * The gates, in the order they run and each with what it costs to get wrong:
 *
 *  1. THE MAJORITY BASE, AND IT IS NEVER DEFAULTED. A motion whose base this
 *     build cannot read has no ratified sentence to put above the rows, and
 *     `?? 'present-and-voting'` would put the LOWEST of the three
 *     constitutional bars on a two-thirds amendment — the worst wrong sentence
 *     this product can print, and wrong in the direction that reassures. That
 *     idiom existed on the count path (`count-bundle.ts:170`) and was removed;
 *     it does not come back here.
 *  2. THE POSITION COLUMNS MUST ALL BE NULL. `ballot_item_motion_columns_iff_motion`
 *     is an IFF, so a motion carrying an electorate, seats or a method is a row
 *     the schema does not produce — and a paper built from half of each set is
 *     the shape a screen must not improvise around.
 *  3. THE INSTRUMENT. Absent, empty or nothing but whitespace is REFUSED: a
 *     title is not a motion, and a member cannot consent to words they have not
 *     been shown. The test is the same split the paper renders from, so the gate
 *     and the render cannot disagree about what "empty" means.
 *  4. COUNTABILITY — see the `uncountable` copy above. Last, because it is a
 *     fact about the BALLOT rather than about a motion this screen could read.
 */
function inspectMotion(item: CastItem, ballot: CastPaperView['ballot']): Inspection {
  if (!isMajorityBase(item.majorityBase)) return { kind: 'unsupported', reason: 'unreadable' };
  if (
    item.electorate !== null ||
    item.seats !== null ||
    item.alternateSeats !== null ||
    item.method !== null
  ) {
    return { kind: 'unsupported', reason: 'unreadable' };
  }
  const paragraphs = instrumentParagraphs(item.instrument ?? '');
  if (paragraphs.length === 0) return { kind: 'unsupported', reason: 'unreadable' };
  if (item.majorityBase === 'summoned-per-house' && ballot.houseRule !== 'separate-chambers') {
    return { kind: 'unsupported', reason: 'uncountable' };
  }
  return {
    kind: 'motion',
    motion: {
      itemId: item.itemId,
      title: item.title,
      base: item.majorityBase,
      paragraphs,
      // A name the record does not hold draws no row rather than a term beside
      // a blank, so an empty string is folded into the same absence as NULL.
      mover: nameOrNull(item.mover),
      seconder: nameOrNull(item.seconder),
    },
  };
}

/** A caption the record actually holds, or nothing. Whitespace is not a name. */
function nameOrNull(value: string | null): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/** The items on this paper that this House may not decide, in the agenda order
 *  the route sent them. Listed on the screen, never omitted from it — see
 *  {@link UnmarkableItems}. */
function unmarkableItems(paper: CastPaperView): CastItem[] {
  return paper.items.filter((item) => !item.entitled);
}

/**
 * A submit-time refusal, as an ACT token. **Deliberately lossy**: the wire code
 * carries more detail than these arms, and it does not go into a live region on
 * a ballot paper — the whole point of {@link CastAct} is that nothing which came
 * off the wire can be spoken there. What a member needs at that moment is
 * whether anything was recorded and whether their paper survived, and every arm
 * says exactly that. The operator's detail survives where it is useful and safe:
 * on the whole-screen refusal, which is reached with no paper on the screen.
 *
 * `blank` picks between two sets of otherwise identical sentences. It is which
 * SCREEN is asking, never anything about the marks: the blank warning has no
 * rows on it, so "your marks are still here" would be a claim about elements
 * that are not there — see {@link CastAct}.
 */
function refusalAct(code: string, blank: boolean): CastAct {
  if (code === 'ballot_not_open') return 'ballot-closed';
  return blank ? 'blank-refused' : 'refused';
}

/**
 * THE MARKED ROW, AS THE WIRE'S OWN BODY — `{ itemId, choice }`, with
 * `candidateId` iff the choice names one. `null` means nothing on this paper is
 * marked, which is not a submission and is answered on the screen.
 *
 * **THE VOCABULARY IS CHOSEN BY THE SUBJECT'S KIND, WHICH IS HOW THE SERVER
 * CHOOSES IT TOO.** `recordCast` tests a motion's choice against
 * `MOTION_CHOICES` and a position's against `POSITION_CHOICES` — they share
 * `abstain` and `blank` and nothing else — so an `aye` sent to an election earns
 * `422 item_not_markable`. Here the branch reads the rows for the kind that was
 * narrowed at issue time, so the wrong vocabulary is not merely refused; it is
 * unreachable.
 *
 * There is no `paperId` in this body and there must never be one: the route
 * resolves the paper through `paper_claim` for this elector, which is what makes
 * cross-elector mark injection unexpressible rather than merely refused.
 */
function markFor(subject: Subject, marked: string | null): CastMark | null {
  if (subject.kind === 'motion') {
    // The SAME list the paper drew its rows from — one module constant, so the
    // row the member touched and the mark sent to the server cannot be two
    // lists that agree until the day they do not.
    const row = MOTION_ROWS.find((candidate) => candidate.key === marked);
    return row === undefined ? null : { itemId: subject.motion.itemId, ...row.mark };
  }
  const row = positionRows(subject.item.candidates).find((candidate) => candidate.key === marked);
  return row === undefined ? null : { itemId: subject.item.itemId, ...row.mark };
}

interface Props {
  ballotId: string;
  /** Read for the header's name and for the "you are voting as" line. It
   *  authorises nothing: the routes re-resolve standing from the roll on every
   *  request they serve. */
  identity: Identity;
  onBack: () => void;
}

export function CastPage({ ballotId, identity, onBack }: Props): JSX.Element {
  const [screen, setScreen] = useState<Screen>({ status: 'loading' });
  /** The marked row's key. Local, and it never leaves this component until the
   *  member submits. */
  const [marked, setMarked] = useState<string | null>(null);
  const [act, setAct] = useState<CastAct>('idle');

  const load = useCallback(async (): Promise<void> => {
    setScreen({ status: 'loading' });
    setMarked(null);
    setAct('idle');
    try {
      const paper = await api.issuePaper(ballotId);
      // The mode is narrowed BEFORE anything is drawn, because the rehearsal
      // band is not optional decoration and a band keyed on an unchecked string
      // fails by disappearing.
      const mode = asBallotMode(paper.ballot.mode);
      // Narrowed here too, and for the confirmation panel alone: whether this
      // ballot is published under the members' names is a sentence that has to
      // be true after the paper has gone. See `asSecrecy` in ../paper.
      const secrecy = asSecrecy(paper.ballot.secrecy);
      if (paper.paper.recorded) {
        // A RESUMED paper whose vote is already recorded. The route deliberately
        // sends no marks at all — the only state in which it could is this one,
        // where returning them would restate the vote on screen and in the
        // screen reader. The member is told the fact and shown nothing.
        setScreen({ status: 'recorded', mode, secrecy, already: true });
        return;
      }
      if (mode === null) {
        setScreen({ status: 'unsupported', reason: 'unreadable' });
        return;
      }
      const checked = inspect(paper);
      if (checked.kind === 'unsupported') {
        setScreen({ status: 'unsupported', reason: checked.reason });
        return;
      }
      if (checked.kind === 'none-entitled') {
        setScreen({ status: 'unmarkable', paper, mode });
        return;
      }
      setScreen({ status: 'marking', paper, subject: checked, mode });
    } catch (err) {
      if (err instanceof DecidesApiError && err.code === 'already_voted') {
        // NO PAPER WAS EVER SEEN, so this screen knows neither the mode nor
        // whether the ballot was secret — and says nothing about either rather
        // than guessing at both.
        setScreen({ status: 'recorded', mode: null, secrecy: null, already: true });
        return;
      }
      setScreen({
        status: 'refused',
        code: err instanceof DecidesApiError ? err.code : 'unreachable',
      });
    }
  }, [ballotId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * THE ONE PLACE THIS SCREEN WRITES — shared by the marked submit and the blank
   * return, so the two cannot answer the same refusal differently.
   *
   * `blank` decides only which of two matched pairs of sentences is spoken (see
   * {@link refusalAct}); every disposition below is identical for both acts.
   *
   * `mode` and `secrecy` are carried in from the paper that was on screen, so
   * the panel that replaces it describes the ballot the member actually voted
   * in — both are ballot-level facts, identical for every member, and neither
   * can be derived from the mark.
   */
  const record = useCallback(
    async (
      send: () => Promise<unknown>,
      mode: BallotMode,
      secrecy: PaperSecrecy | null,
      blank: boolean,
    ): Promise<void> => {
      setAct('submitting');
      try {
        await send();
        setAct('idle');
        setScreen({ status: 'recorded', mode, secrecy, already: false });
      } catch (err) {
        if (err instanceof DecidesApiError) {
          if (err.code === 'already_voted') {
            setAct('idle');
            setScreen({ status: 'recorded', mode, secrecy, already: true });
            return;
          }
          // THE PAPER ON SCREEN IS STALE — this elector is now entitled to
          // nothing on this ballot, which A2 could not have said and which means
          // the roll moved under them (a mid-sitting substitution). Take the
          // paper down rather than leave rows above a line contradicting them;
          // the copy, and the whole argument, is on REFUSAL_COPY's entry.
          if (err.code === 'no_entitled_items') {
            setAct('idle');
            setScreen({ status: 'refused', code: err.code });
            return;
          }
          setAct(refusalAct(err.code, blank));
          return;
        }
        // A network failure, and the one the failure copy was written for: the
        // fact first, the paper untouched, and no button offered ahead of
        // either. The member presses again when they are ready.
        setAct(blank ? 'blank-unreachable' : 'unreachable');
      }
    },
    [],
  );

  const submit = useCallback(async (): Promise<void> => {
    if (screen.status !== 'marking') return;
    const mark = markFor(screen.subject, marked);
    if (mark === null) {
      // NOTHING LEAVES THE DEVICE. An unmarked submit is still not sent as a
      // blank paper, and that is unchanged by the 2026-08-27 ruling: a blank is
      // permanent, and pressing Submit with nothing marked is exactly what a
      // mis-tap looks like. A member who MEANS to return a blank paper has a
      // deliberate act for it, with a warning in front of it, and the status
      // line now names that act rather than telling them a row is their only
      // way forward.
      setAct('nothing-marked');
      return;
    }
    await record(
      () => api.recordMarks(ballotId, [mark]),
      screen.mode,
      asSecrecy(screen.paper.ballot.secrecy),
      false,
    );
  }, [ballotId, marked, screen, record]);

  /** Open the blank-paper warning. **IT SENDS NOTHING.** The only thing this
   *  does is take the rows off the screen and put the warning in their place. */
  const askBlank = useCallback((): void => {
    if (screen.status !== 'marking') return;
    setAct('idle');
    setScreen({
      status: 'confirm-blank',
      paper: screen.paper,
      subject: screen.subject,
      mode: screen.mode,
    });
  }, [screen]);

  /** Back to the paper, with the mark the member left on it — `marked` is not in
   *  the `Screen` union, so backing out of the warning costs them nothing. */
  const cancelBlank = useCallback((): void => {
    if (screen.status !== 'confirm-blank') return;
    setAct('idle');
    setScreen({
      status: 'marking',
      paper: screen.paper,
      subject: screen.subject,
      mode: screen.mode,
    });
  }, [screen]);

  /** THE BLANK CAST — `{ marks: [] }`, which is the server's own model of a
   *  returned paper: one `blank` row per ENTITLED item, no row for the rest. It
   *  is reachable from this one confirmed press and from nowhere else, and it is
   *  the same act on both papers: `blank` is a word in the motion vocabulary too
   *  (`MOTION_CHOICES`), and the envelope carries no item at all. */
  const returnBlank = useCallback(async (): Promise<void> => {
    if (screen.status !== 'confirm-blank') return;
    await record(
      () => api.returnBlankPaper(ballotId),
      screen.mode,
      asSecrecy(screen.paper.ballot.secrecy),
      true,
    );
  }, [ballotId, screen, record]);

  if (screen.status === 'loading') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <p role="status">Fetching your ballot paper…</p>
      </Frame>
    );
  }

  if (screen.status === 'refused') {
    return (
      <Frame identity={identity} onBack={onBack}>
        {/* The heading covers a refusal AND an unreachable server, because at
            this point the screen does not always know which it met — and "there
            is no paper for you" would be a statement about the reader's standing
            made on the strength of bad wifi. The sentence below it draws the
            distinction; the heading does not pre-empt it. */}
        <h1 className="ballot-title">Your ballot paper could not be opened</h1>
        <p className="alert">{refusalSentence(screen.code)}</p>
        <button type="button" className="btn btn-ghost" onClick={() => void load()}>
          Try again
        </button>
      </Frame>
    );
  }

  if (screen.status === 'unsupported') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <h1 className="ballot-title">This paper cannot be shown here</h1>
        <p className="alert">{UNSUPPORTED_COPY[screen.reason]}</p>
      </Frame>
    );
  }

  if (screen.status === 'recorded') {
    return (
      <Frame identity={identity} onBack={onBack} mode={screen.mode}>
        <Recorded already={screen.already} secrecy={screen.secrecy} />
      </Frame>
    );
  }

  // THE BLANK-PAPER WARNING REPLACES THE PAPER — neither paper is rendered on
  // this arm, so there is no marked row, no stripe and no candidate name on
  // the screen while the highest-exposure moment of the flow is on it. The
  // component takes no prop through which a mark could reach it either; both
  // halves of that argument are at the head of ./BlankConfirm.tsx.
  if (screen.status === 'confirm-blank') {
    return (
      <Frame identity={identity} onBack={onBack} mode={screen.mode}>
        <BlankConfirm
          act={act}
          submitting={act === 'submitting'}
          onConfirm={() => void returnBlank()}
          onCancel={cancelBlank}
        />
      </Frame>
    );
  }

  // NOTHING ON THIS PAPER IS THIS HOUSE'S. The items are shown — every paper on
  // one ballot carries the same items, so the shape of the paper never says
  // which House is holding it — and there is NO CONTROL OF ANY KIND on the
  // screen: no submit, and no "return this paper blank" either.
  //
  // THAT SECOND ABSENCE IS NOT AN OVERSIGHT, and it is the one a later change is
  // likeliest to "fix". Returning a blank paper is lawful for a member who has
  // something to mark; here the route refuses it — `recordCast` answers 409
  // `no_entitled_items` before it prepares a single statement — so a blank
  // control on this screen would be a control the database refuses, which is the
  // thing `mark_subject_is_in_the_item_electorate`'s own note forbids a surface
  // from offering. And the harm it guards is the `participation` row: Book One,
  // append-only, never removed, and there is no event that could take back a
  // permanent public statement that this member voted in a ballot they held no
  // vote in.
  if (screen.status === 'unmarkable') {
    return (
      <Frame identity={identity} onBack={onBack} mode={screen.mode}>
        <section className="ballot">
          <p className="ballot-eyebrow">
            <span>{screen.paper.ballot.title}</span>
          </p>
          <h1 className="ballot-title">There is nothing on this ballot for you to mark</h1>
          <p className="ballot-instrument-text">
            Every position on this ballot is elected by the other House (Standing Order 15.8), so
            there is no vote of yours to record here. Nothing has been recorded, and nothing you do
            on this screen would be.
          </p>
          <hr className="ballot-rule" />
          <UnmarkableItems heading="On this ballot" items={screen.paper.items} />
        </section>
      </Frame>
    );
  }

  // THE TWO PAPERS. One flow, one set of handlers, and the kind decides only
  // which renderer draws it.
  //
  // THE MOTION PAPER TAKES NO `unmarkable` LIST, and that is by construction
  // rather than by omission: entitlement is per-ITEM and a motion's electorate
  // is NULL, so `isEntitledToMark` returns true for every elector on the roll
  // (`@cca/decides-worker/src/cast.ts`) and every item on a motion ballot is
  // this reader's. The SO 15.8 block exists because a CLERGY seat and a LAY seat
  // can sit on one election ballot; there is no motion equivalent of that, and a
  // heading reading "Also on this ballot" over an empty list would be chrome.
  if (screen.subject.kind === 'motion') {
    return (
      <Frame identity={identity} onBack={onBack} mode={screen.mode}>
        <MotionPaper
          paper={screen.paper}
          motion={screen.subject.motion}
          voterLabel={identity.email}
          marked={marked}
          onMark={setMarked}
          onSubmit={() => void submit()}
          onReturnBlank={askBlank}
          submitting={act === 'submitting'}
          act={act}
        />
      </Frame>
    );
  }

  return (
    <Frame identity={identity} onBack={onBack} mode={screen.mode}>
      <PositionPaper
        paper={screen.paper}
        item={screen.subject.item}
        rows={positionRows(screen.subject.item.candidates)}
        unmarkable={unmarkableItems(screen.paper)}
        voterLabel={identity.email}
        marked={marked}
        onMark={setMarked}
        onSubmit={() => void submit()}
        onReturnBlank={askBlank}
        submitting={act === 'submitting'}
        act={act}
      />
    </Frame>
  );
}

/** The page frame every state shares, so none of them restates the layout. The
 *  mode band is drawn only where the mode is known — a rehearsal band is not
 *  optional decoration, and a guessed one is worse than none. */
function Frame({
  children,
  identity,
  onBack,
  mode,
}: {
  children: ReactNode;
  identity: Identity;
  onBack: () => void;
  mode?: BallotMode | null;
}): JSX.Element {
  const band = mode ?? null;
  return (
    <div className="ballot-app min-dvh">
      <PortalHeader portalName={portalNameFor(identity)} />
      {band === null ? null : <ModeBand mode={band} />}
      <main className="ballot-main">
        <div className="page-narrow">
          <button type="button" className="btn btn-ghost ballot-back" onClick={onBack}>
            Back to your ballots
          </button>
          {children}
        </div>
      </main>
      {band === null ? null : <ModeBand mode={band} repeat />}
    </div>
  );
}

/**
 * The vote is in. **This panel confirms the ACT and never the content** — there
 * is no candidate name on it, no label, no mark, and no prop through which one
 * could arrive.
 *
 * ── `secrecy` IS A BALLOT FACT, NOT A CHANNEL, AND THE ARGUMENT IS WRITTEN
 *    HERE SO THE NEXT READER DOES NOT HAVE TO RECONSTRUCT IT ─────────────────
 * The paragraph below used to assert _"The ballot is secret"_ unconditionally,
 * and on an attributable motion that is a FALSE STATEMENT ABOUT PUBLICATION made
 * on the confirmation of a binding vote — told to the one person who can no
 * longer do anything about it. So the panel is now told which kind of ballot it
 * is confirming.
 *
 * The prop passes the shape test this component's rule is really about. It is
 * `ballot.secrecy` — identical for every member of the sitting, decided when the
 * ballot was built, and already SHOWN on the paper above the rows before the
 * mark was made. Nothing about it varies with, is derived from, or could be
 * inverted to recover, what this member marked; it is not a string the caller
 * composes but a narrowed two-word union plus `null`. The rule that matters is
 * unchanged and is worth restating in the form a reviewer should check it: there
 * is still no prop here through which a MARK could arrive — no `marked`, no
 * `choice`, no `label`, no `children`.
 *
 * `null` renders NO secrecy paragraph at all. It is the `409 already_voted`
 * road, where no paper was ever issued to this screen, so the honest answer to
 * "was this ballot secret?" is that this screen does not know — and a guess in
 * either direction is a false statement about publication.
 *
 * IT IS NOT A LIVE REGION, and that is deliberate rather than an oversight. The
 * paper it replaced has been unmounted, so a live region here would be a NEW
 * element carrying its first message — which several screen readers do not
 * announce at all. The standard answer for a view that has replaced another is
 * to move focus to its heading, which is what happens below: the heading is the
 * announcement, and it is a sentence about the act.
 *
 * `tabIndex={-1}` makes the heading programmatically focusable without putting
 * it in the tab order.
 *
 * It offers NO control of its own. The way back is the frame's one button,
 * directly above it — a second button with the same words would be two places to
 * press for one act, and there is nothing else a member can do here: the vote is
 * in, and this screen is deliberately unable to change it.
 */
function Recorded({
  already,
  secrecy,
}: {
  already: boolean;
  secrecy: PaperSecrecy | null;
}): JSX.Element {
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);

  return (
    <section className="ballot ballot-recorded">
      <h1 className="ballot-title" tabIndex={-1} ref={heading}>
        {already ? 'Your vote is already recorded.' : 'Your vote has been recorded.'}
      </h1>
      <p className="ballot-recorded-text">
        {already
          ? 'This ballot already holds your paper. Nothing further is needed, and nothing you do now can change it.'
          : 'Nothing further is needed.'}
      </p>
      {/* THE SECRET BALLOT — the registrar's wording, on the ballot it was
          written for. ONE sentence: the trailing clause about the moment of
          confirmation being when a paper is most likely to be overlooked was
          DELETED from BOTH arms at his direction (2026-09-01, after he cast the
          first live vote and read this screen), and is not to be restored to
          either. */}
      {secrecy === 'secret' && (
        <p className="ballot-recorded-text">
          This screen does not show you what you marked, and it never will.
        </p>
      )}
      {/* AN ATTRIBUTABLE MOTION. It says the true thing about publication, and
          then keeps the same discipline about the screen itself: a vote published
          in the minutes later is still not printed back onto this phone now. The
          same trailing clause was deleted here as in the secret arm above. */}
      {secrecy === 'attributable' && (
        <p className="ballot-recorded-text">
          This motion was taken attributably, not by secret ballot: your vote is recorded against
          your name and will be published with the result. This screen still does not show you what
          you marked.
        </p>
      )}
    </section>
  );
}
